import { put, call, takeLatest, select } from 'redux-saga/effects';
import get from 'lodash.get';
import moment from 'moment';
import { AxiosError } from 'axios';
import { API } from '../api/base';
import {
  types,
  ReserveTicketsAsyncRequest,
  ReserveMembershipAsyncRequest,
  ReleaseCartAsyncRequest,
  Cart,
  GiftVoucher,
  ReserveTicketsPayload,
  ReserveMembershipPayload,
  RemoveTicketsPayload,
  PurchasePayload,
  PurchaseCartResponse,
  ApplyPromoCodeAsyncRequest,
  ApplyGiftVoucherAsyncRequest,
  RemoveTicketsAsyncRequest,
  ApplyDonationAsyncRequest,
  RemoveDonationAsyncRequest
} from './types';
import { types as customerTypes, selectors as customerSelectors, Customer } from 'services/customer';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { selectors as cityPassSelectors, CityPassParams } from 'services/cityPass';
import { types as hardwareTypes, selectors as hardwareSelectors, PaymentDevice } from 'services/hardware';
import { selectors as cartSelectors } from './selectors';
import { getErrorMessage, getErrorCode, getErrorMessageCode } from 'services/api/normalizer';
import i18n from 'services/translation/i18n';

// RESERVE TICKETS
export function* reserveTicketsAsync({ payload }: ReserveTicketsAsyncRequest) {
  yield put({ type: types.RESERVE_TICKETS_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const cart: Cart | null = yield select(cartSelectors.selectCart);
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);
    const { membershipParams } = yield select(ticketingSelectors.selectMembershipDetails);

    const reserveTicketsPayload: ReserveTicketsPayload = {
      items: payload,
      addMoreParams: cart && cart.addMoreParams ? cart.addMoreParams : undefined,
      cityPassParams,
      membershipParams
    };

    const cartApi = new API(`ticketing/tickets/reserve?session=${session}`);
    const response: Cart = yield call(cartApi.create, reserveTicketsPayload);

    yield put({ type: types.RESERVE_TICKETS_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);
    yield put({ type: types.RESERVE_TICKETS_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.RESERVE_TICKETS_ASYNC.PENDING, payload: false });
  }
}

export function* reserveMembershipAsync({ payload }: ReserveMembershipAsyncRequest) {
  yield put({ type: types.RESERVE_MEMBERSHIP_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const { membershipParams } = yield select(ticketingSelectors.selectMembershipDetails);
    const cart: Cart | null = yield select(cartSelectors.selectCart);
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);

    const reservePayload: ReserveMembershipPayload = {
      ...payload,
      addMoreParams: cart && cart.addMoreParams ? cart.addMoreParams : undefined,
      cityPassParams,
      membershipParams
    };
    const reserveApi = new API(`ticketing/membership/reserve?session=${session}`);
    const response: Cart = yield call(reserveApi.create, reservePayload);

    // save the data from the 1st type as the primary customer
    const customer = get(payload, 'types[0].fields');
    if (customer) {
      yield put({ type: customerTypes.SAVE_CUSTOMER_ASYNC.SUCCESS, payload: customer });
    }

    yield put({ type: types.RESERVE_MEMBERSHIP_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);
    yield put({ type: types.RESERVE_MEMBERSHIP_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.RESERVE_MEMBERSHIP_ASYNC.PENDING, payload: false });
  }
}

// RELEASE TICKETS AND CLEAR CART
export function* releaseCartAsync({ payload }: ReleaseCartAsyncRequest) {
  yield put({ type: types.RELEASE_CART_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);

    const cartApi = new API(`ticketing/tickets/release?session=${session}`);
    yield call(cartApi.create, payload);

    yield put({ type: types.RELEASE_CART_ASYNC.SUCCESS });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);
    yield put({ type: types.RELEASE_CART_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.RELEASE_CART_ASYNC.PENDING, payload: false });
  }
}

// SEND PURCHASE DATA TO API
export function* purchaseCartAsync() {
  yield put({ type: types.PURCHASE_CART_ASYNC.PENDING, payload: true });
  const paymentData: PaymentDevice = yield select(hardwareSelectors.selectPaymentDevice);
  const { checkoutParams, releaseParams }: Cart = yield select(cartSelectors.selectCart);
  const totalToCharge: number = yield select(cartSelectors.selectCartTotalToCharge);
  const { voucherCode }: GiftVoucher = yield select(cartSelectors.selectGiftVoucher);
  const giftVoucherAmountToUse: number | undefined = yield select(cartSelectors.selectGiftVoucherAmountToUse);

  let data = paymentData;
  if (totalToCharge === 0) {
    data = {
      ...data,
      saleData: {
        amount: '0',
        orderId: releaseParams && releaseParams.orderId ? releaseParams.orderId : '',
        cardNumber: '',
        cardType: '',
        cardholderName: '',
        code: '',
        currency: '',
        description: '',
        expiryDate: '',
        transactionDate: ''
      }
    };
  }

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const customer: Customer = yield select(customerSelectors.selectCustomer);
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);

    const cartApi = new API(`ticketing/tickets/purchase?session=${session}`);
    let checkoutPayload: PurchasePayload = {
      data,
      checkoutParams: {
        ...checkoutParams,
        giftVoucherAmount: giftVoucherAmountToUse,
        giftVoucherNumber: voucherCode
      },
      customer: customer ? customer : undefined
    };
    if (cityPassParams) {
      checkoutPayload = {
        ...checkoutPayload,
        cityPassParams: {
          ...cityPassParams,
          usageDate: moment().format()
        }
      };
    }

    const checkoutCart: PurchaseCartResponse = yield call(cartApi.create, checkoutPayload);
    yield put({ type: types.PURCHASE_CART_ASYNC.SUCCESS, payload: checkoutCart });
  } catch (err) {
    const code = getErrorCode(err as AxiosError);
    const messageCode = getErrorMessageCode(err as AxiosError);

    const config: Configuration = yield select(configSelectors.selectConfiguration);
    const ticketingPath: string = yield select(ticketingSelectors.selectTicketingConfigPath);
    const support = get(config, `${ticketingPath}.supportName.${i18n.language}`) || i18n.t('commonText.support');
    const cartLabel = get(config, `configuration.labels.cart.${i18n.language}`) || i18n.t('cart.cartLabel');
    const orderId = releaseParams && releaseParams.orderId ? releaseParams.orderId : '';
    let message = i18n.t('saleError.defaultPurchaseError', { support, orderId });

    // Attempt to void the transaction if txn amount greater than zero
    if (totalToCharge > 0) {
      yield put({ type: hardwareTypes.PAYMENT_VOID_ASYNC.REQUEST, payload: get(data, ['saleData', 'voidParams']) });
    }

    if ((code as number) >= 400 && (code as number) < 500) {
      if (messageCode === 1) {
        message = i18n.t('saleError.invalidCardType');
      } else if (messageCode === 2) {
        message = i18n.t('saleError.wrongAmount', { cartLabel });
      }
      yield put({ type: types.PURCHASE_CART_ASYNC.ERROR, payload: message });
    } else {
      yield put({ type: types.PURCHASE_CART_ASYNC.FATAL_ERROR, payload: message });
    }
  } finally {
    yield put({ type: types.PURCHASE_CART_ASYNC.PENDING, payload: false });
  }
}

export function* applyPromoCodeAsync({ payload }: ApplyPromoCodeAsyncRequest) {
  yield put({ type: types.APPLY_PROMO_CODE_ASYNC.PENDING, payload: true });

  try {
    const { applyPromoParams }: Cart = yield select(cartSelectors.selectCart);
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);

    const applyPromoPayload = {
      promoCode: payload,
      applyPromoParams,
      cityPassParams
    };

    const api = new API('ticketing/promoCode/cart');
    const response: Cart = yield call(api.create, applyPromoPayload);

    yield put({
      type: types.APPLY_PROMO_CODE_ASYNC.SUCCESS,
      payload: {
        processed: true,
        updatedCart: response
      }
    });
  } catch (err) {
    const config: Configuration = yield select(configSelectors.selectConfiguration);
    const cartItemLabel = get(config, `configuration.labels.cartItem.${i18n.language}`) || i18n.t('cart.cartItemLabel');
    const cartLabel = get(config, `configuration.labels.cart.${i18n.language}`) || i18n.t('cart.cartLabel');

    let message = i18n.t('sagaError.applyPromo');
    const errCode = getErrorMessageCode(err as AxiosError);
    if (errCode) {
      if (errCode === 404) {
        message = i18n.t('sagaError.unknownCode');
      } else if (errCode === 409) {
        message = i18n.t('sagaError.noDiscount', { items: cartItemLabel, cartLabel });
      } else if (errCode === 499) {
        message = i18n.t('sagaError.duplicateCode');
      }
    }

    yield put({ type: types.APPLY_PROMO_CODE_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.APPLY_PROMO_CODE_ASYNC.PENDING, payload: false });
  }
}

export function* applyGiftVoucherAsync({ payload }: ApplyGiftVoucherAsyncRequest) {
  yield put({ type: types.APPLY_GIFT_VOUCHER_ASYNC.PENDING, payload: true });

  try {
    const api = new API('ticketing/giftVoucher');
    const response: GiftVoucher = yield call(api.create, { giftCertificateNumber: payload });

    yield put({
      type: types.APPLY_GIFT_VOUCHER_ASYNC.SUCCESS,
      payload: {
        ...response,
        voucherCode: payload,
        processed: true
      }
    });
  } catch (err) {
    let message = i18n.t('sagaError.applyGiftVoucher');
    const errCode = getErrorMessageCode(err as AxiosError);
    if (errCode) {
      if (errCode === 404) {
        message = i18n.t('sagaError.unknownCode');
      } else if (errCode === 401) {
        message = i18n.t('sagaError.insufficientBalance');
      }
    }

    yield put({ type: types.APPLY_GIFT_VOUCHER_ASYNC.ERROR, payload: { error: message } });
  } finally {
    yield put({ type: types.APPLY_GIFT_VOUCHER_ASYNC.PENDING, payload: false });
  }
}

export function* removeTicketsAsync({ payload }: RemoveTicketsAsyncRequest) {
  yield put({ type: types.REMOVE_TICKETS_FROM_CART.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const { membershipParams } = yield select(ticketingSelectors.selectMembershipDetails);
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);

    const removeTicketsPayload: RemoveTicketsPayload = {
      items: payload,
      cityPassParams,
      membershipParams
    };

    const api = new API(`ticketing/tickets/remove?session=${session}`);
    const response: Cart = yield call(api.create, removeTicketsPayload);

    yield put({ type: types.REMOVE_TICKETS_FROM_CART.SUCCESS, payload: response });
  } catch (err) {
    const config: Configuration = yield select(configSelectors.selectConfiguration);
    const cartItemLabel = get(config, `configuration.labels.cartItem.${i18n.language}`) || i18n.t('cart.cartItemLabel');
    const cartLabel = get(config, `configuration.labels.cart.${i18n.language}`) || i18n.t('cart.cartLabel');
    const message = i18n.t('sagaError.removeTickets', { item: cartItemLabel, cartLabel });

    yield put({ type: types.REMOVE_TICKETS_FROM_CART.ERROR, payload: { error: message } });
  } finally {
    yield put({ type: types.REMOVE_TICKETS_FROM_CART.PENDING, payload: false });
  }
}

export function* applyDonationAsync({ payload }: ApplyDonationAsyncRequest) {
  yield put({ type: types.APPLY_DONATION_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const { membershipParams } = yield select(ticketingSelectors.selectMembershipDetails);
    const cart: Cart = yield select(cartSelectors.selectCart);
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);

    const donationPayload = {
      amount: payload,
      donationParams: (cart && cart.donationParams) || {},
      cityPassParams,
      membershipParams
    };

    const donationApi = new API(`ticketing/donation?session=${session}`);
    const response: Cart = yield call(donationApi.create, donationPayload);

    yield put({
      type: types.APPLY_DONATION_ASYNC.SUCCESS,
      payload: response
    });
  } catch (err) {
    const config: Configuration = yield select(configSelectors.selectConfiguration);
    const cartLabel = get(config, `configuration.labels.cart.${i18n.language}`) || i18n.t('cart.cartLabel');
    const message = i18n.t('sagaError.applyDonation', { cartLabel });
    yield put({ type: types.APPLY_DONATION_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.APPLY_DONATION_ASYNC.PENDING, payload: false });
  }
}

export function* removeDonationAsync({ payload }: RemoveDonationAsyncRequest) {
  yield put({ type: types.REMOVE_DONATION_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const { membershipParams } = yield select(ticketingSelectors.selectMembershipDetails);
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);

    const removePayload = {
      removeParams: payload,
      cityPassParams,
      membershipParams
    };

    const removeDonationApi = new API(`ticketing/donation/remove?session=${session}`);
    const response: Cart = yield call(removeDonationApi.create, removePayload);

    yield put({ type: types.REMOVE_DONATION_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);
    yield put({ type: types.REMOVE_DONATION_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.REMOVE_DONATION_ASYNC.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.RESERVE_TICKETS_ASYNC.REQUEST, reserveTicketsAsync),
  takeLatest(types.RESERVE_MEMBERSHIP_ASYNC.REQUEST, reserveMembershipAsync),
  takeLatest(types.RELEASE_CART_ASYNC.REQUEST, releaseCartAsync),
  takeLatest(types.PURCHASE_CART_ASYNC.REQUEST, purchaseCartAsync),
  takeLatest(types.APPLY_PROMO_CODE_ASYNC.REQUEST, applyPromoCodeAsync),
  takeLatest(types.APPLY_GIFT_VOUCHER_ASYNC.REQUEST, applyGiftVoucherAsync),
  takeLatest(types.REMOVE_TICKETS_FROM_CART.REQUEST, removeTicketsAsync),
  takeLatest(types.APPLY_DONATION_ASYNC.REQUEST, applyDonationAsync),
  takeLatest(types.REMOVE_DONATION_ASYNC.REQUEST, removeDonationAsync)
];
